import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, AlertTriangle, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";

const chf = (v) => Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AiUsageSettingsCard({ companyId }) {
  const [data, setData] = useState(null);
  const [buying, setBuying] = useState(null);
  const [polling, setPolling] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    try { const r = await apiClient.get(`/companies/${companyId}/ai-budget`); setData(r.data); } catch { /* ignore */ }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  // Rückkehr von Stripe: ?ai_credit_session=… → Status pollen, Gutschrift anzeigen
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("ai_credit_session");
    if (params.get("ai_credit_cancelled")) { toast.info("Kauf abgebrochen"); window.history.replaceState({}, "", `${window.location.pathname}#section-ai-usage`); }
    if (!sid || !companyId) return;
    let tries = 0; let stop = false;
    setPolling(true);
    const tick = async () => {
      if (stop) return;
      try {
        const r = await apiClient.get(`/companies/${companyId}/ai-credits/checkout-status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success(`KI-Credits gutgeschrieben – neues Guthaben CHF ${chf(r.data.credits_chf)}`);
          setPolling(false); load(); window.history.replaceState({}, "", `${window.location.pathname}#section-ai-usage`); return;
        }
        if (r.data.status === "expired") { toast.error("Zahlung abgelaufen"); setPolling(false); return; }
      } catch { /* retry */ }
      if (++tries < 8) setTimeout(tick, 2000); else { setPolling(false); toast.info("Zahlung noch nicht bestätigt – Guthaben wird nach Eingang automatisch gutgeschrieben"); }
    };
    tick();
    return () => { stop = true; };
  }, [companyId, load]);

  const buy = async (pkg) => {
    setBuying(pkg.id);
    try {
      const r = await apiClient.post(`/companies/${companyId}/ai-credits/checkout`, { package_id: pkg.id, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Kauf konnte nicht gestartet werden"); setBuying(null); }
  };

  if (!data) return null;
  const pct = data.pct;
  const bar = data.blocked ? "bg-rose-500" : pct != null && pct >= 80 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <Card className="p-5 border border-slate-200 bg-white" data-testid="ai-usage-settings-card">
      <div className="flex items-start gap-3 mb-4">
        <Sparkles className="w-5 h-5 text-slate-700 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-900">KI-Nutzung &amp; Credits</h3>
          <p className="text-xs text-slate-500">Verbrauch des KI-Assistenten und der Beleg-KI pro Monat, Ihr monatliches KI-Budget, gekaufte Credits und die weiterverrechneten Positionen.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <div className="rounded-md border border-slate-200 p-3" data-testid="ai-usage-month-box">
          <div className="text-[11px] text-slate-500">Verbrauch {data.year_month}</div>
          <div className="text-lg font-semibold text-slate-900" data-testid="ai-usage-month-used">CHF {chf(data.used_chf)}</div>
          <div className="text-[11px] text-slate-500">{data.calls} KI-Aufrufe{data.markup_pct ? ` · inkl. ${data.markup_pct} % Aufschlag` : ""}</div>
        </div>
        <div className="rounded-md border border-slate-200 p-3" data-testid="ai-usage-budget-box">
          <div className="text-[11px] text-slate-500">Monatsbudget</div>
          <div className="text-lg font-semibold text-slate-900" data-testid="ai-usage-budget">{data.budget_chf != null ? `CHF ${chf(data.budget_chf)}` : "kein Deckel"}</div>
          {data.budget_chf != null && (
            <>
              <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden mt-1"><div className={`h-full ${bar}`} style={{ width: `${Math.min(100, pct || 0)}%` }} /></div>
              <div className="text-[11px] text-slate-500 mt-0.5">{pct} % · bei Erreichen: {data.mode === "pause" ? "Assistent pausiert" : "Hinweis"}</div>
            </>
          )}
        </div>
        <div className="rounded-md border border-slate-200 p-3" data-testid="ai-usage-credits-box">
          <div className="text-[11px] text-slate-500">KI-Credits</div>
          <div className="text-lg font-semibold text-slate-900" data-testid="ai-usage-credits">CHF {chf(data.credits_chf)}</div>
          <div className="text-[11px] text-slate-500">werden genutzt, sobald das Budget erreicht ist</div>
        </div>
      </div>

      {data.message && (
        <div className={`mb-4 flex items-start gap-2 rounded-md px-3 py-2 text-xs ${data.blocked ? "bg-rose-50 text-rose-800" : "bg-amber-50 text-amber-800"}`} data-testid="ai-usage-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{data.message}
        </div>
      )}

      {data.packages?.length > 0 && (
        <div className="mb-4" data-testid="ai-credit-packages">
          <div className="text-xs font-medium text-slate-700 mb-1.5 flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> KI-Credits kaufen {polling && <Loader2 className="w-3 h-3 animate-spin" />}</div>
          <div className="flex flex-wrap gap-2">
            {data.packages.map((p) => (
              <button key={p.id} disabled={!data.purchase_available || buying} onClick={() => buy(p)} className="rounded-md border border-slate-200 px-3 py-2 text-left hover:border-slate-400 disabled:opacity-50" data-testid={`ai-credit-buy-${p.id}`}>
                <div className="text-xs font-semibold text-slate-900">{p.name}</div>
                <div className="text-[11px] text-slate-600">CHF {chf(p.credits_chf)} Credits</div>
                <div className="text-xs font-medium text-slate-900 mt-0.5">{buying === p.id ? "…" : `CHF ${chf(p.price_chf)}`}</div>
              </button>
            ))}
          </div>
          {!data.purchase_available && <div className="text-[11px] text-slate-500 mt-1">Kartenzahlung derzeit nicht verfügbar – bitte den Betreiber kontaktieren.</div>}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div data-testid="ai-usage-months">
          <div className="text-xs font-medium text-slate-700 mb-1">Verbrauch pro Monat</div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-500 border-b border-slate-100"><th className="py-1">Monat</th><th className="text-right">Aufrufe</th><th className="text-right">Betrag CHF</th></tr></thead>
            <tbody>
              {data.months.map((m) => <tr key={m.year_month} className="border-b border-slate-50"><td className="py-1">{m.year_month}</td><td className="text-right">{m.calls}</td><td className="text-right font-medium">{chf(m.amount_chf)}</td></tr>)}
              {!data.months.length && <tr><td colSpan={3} className="py-2 text-slate-400">Noch keine KI-Nutzung</td></tr>}
            </tbody>
          </table>
        </div>
        <div data-testid="ai-usage-statements">
          <div className="text-xs font-medium text-slate-700 mb-1">Verrechnete Positionen &amp; Credit-Bewegungen</div>
          <table className="w-full text-xs">
            <tbody>
              {data.statements.map((s) => <tr key={s.id} className="border-b border-slate-50"><td className="py-1">{s.position_text}</td><td className="text-right font-medium">{chf(s.amount_chf)}</td><td className="text-right text-slate-500">{s.status === "verrechnet" ? `Rechnung ${s.invoice_number}` : "übernommen"}</td></tr>)}
              {data.transactions.map((t) => <tr key={t.id} className="border-b border-slate-50"><td className="py-1 text-slate-600">{String(t.created_at).slice(0, 10)} · {t.note}</td><td className={`text-right font-medium ${t.amount_chf < 0 ? "text-rose-700" : "text-emerald-700"}`}>{t.amount_chf > 0 ? "+" : ""}{chf(t.amount_chf)}</td><td className="text-right text-slate-500">Credits</td></tr>)}
              {!data.statements.length && !data.transactions.length && <tr><td className="py-2 text-slate-400">Noch keine Positionen</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}
